import { Transforms } from "slate";

/**
 * Parses markdown text into Slate nodes
 *
 * @param {string} text - The markdown text to parse
 * @returns {Array} Slate nodes
 */
export const parseMarkdown = (text) => {
  const lines = text.split("\n");
  const nodes = [];
  let currentList = null;
  let currentListType = null;

  lines.forEach((line) => {
    // Check for ordered list (e.g., "1. Item")
    const orderedListMatch = line.match(/^\d+\.\s+(.+)$/);
    if (orderedListMatch) {
      const content = parseInlineMarkdown(orderedListMatch[1]);
      const listItem = {
        type: "list-item",
        children: Array.isArray(content) ? content : [{ text: content }],
      };

      if (currentListType === "numbered-list" && currentList) {
        currentList.children.push(listItem);
      } else {
        if (currentList) {
          nodes.push(currentList);
        }
        currentList = {
          type: "numbered-list",
          children: [listItem],
        };
        currentListType = "numbered-list";
      }
      return;
    }

    // Check for unordered list (e.g., "* Item" or "- Item")
    const unorderedListMatch = line.match(/^[*-]\s+(.+)$/);
    if (unorderedListMatch) {
      const content = parseInlineMarkdown(unorderedListMatch[1]);
      const listItem = {
        type: "list-item",
        children: Array.isArray(content) ? content : [{ text: content }],
      };

      if (currentListType === "bulleted-list" && currentList) {
        currentList.children.push(listItem);
      } else {
        if (currentList) {
          nodes.push(currentList);
        }
        currentList = {
          type: "bulleted-list",
          children: [listItem],
        };
        currentListType = "bulleted-list";
      }
      return;
    }

    // If we have a current list and this isn't a list item, close it
    if (currentList) {
      nodes.push(currentList);
      currentList = null;
      currentListType = null;
    }

    // Parse inline markdown for regular paragraphs
    const children = parseInlineMarkdown(line);
    nodes.push({
      type: "paragraph",
      children: Array.isArray(children) ? children : [{ text: line }],
    });
  });

  // Don't forget to add the last list if there is one
  if (currentList) {
    nodes.push(currentList);
  }

  return nodes.length > 0 ? nodes : [{ type: "paragraph", children: [{ text: "" }] }];
};

/**
 * Parses inline markdown formatting (bold, italic, underline)
 *
 * @param {string} text - The text to parse
 * @returns {Array} Array of text nodes with formatting
 */
const parseInlineMarkdown = (text) => {
  const nodes = [];

  // Process in order: bold+italic (***), then bold (**), then italic (*), then underline (__)
  // This prevents overlapping matches

  // First pass: Process ***text*** (bold + italic)
  const boldItalicRegex = /\*\*\*([^*]+)\*\*\*/g;
  let parts = [];
  let lastIndex = 0;
  let match;

  while ((match = boldItalicRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ text: text.substring(lastIndex, match.index), processed: false });
    }
    parts.push({ text: match[1], marks: { bold: true, italic: true }, processed: true });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push({ text: text.substring(lastIndex), processed: false });
  }

  if (parts.length === 0) {
    parts.push({ text: text, processed: false });
  }

  // Second pass: Process **text** (bold) only in unprocessed parts
  const newParts = [];
  parts.forEach((part) => {
    if (part.processed) {
      newParts.push(part);
    } else {
      const boldRegex = /\*\*([^*]+)\*\*/g;
      let lastIdx = 0;
      let m;
      let hasBold = false;

      while ((m = boldRegex.exec(part.text)) !== null) {
        hasBold = true;
        if (m.index > lastIdx) {
          newParts.push({ text: part.text.substring(lastIdx, m.index), processed: false });
        }
        newParts.push({ text: m[1], marks: { bold: true }, processed: true });
        lastIdx = m.index + m[0].length;
      }

      if (hasBold) {
        if (lastIdx < part.text.length) {
          newParts.push({ text: part.text.substring(lastIdx), processed: false });
        }
      } else {
        newParts.push(part);
      }
    }
  });

  parts = newParts.length > 0 ? newParts : parts;

  // Third pass: Process *text* (italic) only in unprocessed parts
  const finalParts = [];
  parts.forEach((part) => {
    if (part.processed) {
      finalParts.push(part);
    } else {
      const italicRegex = /\*([^*]+)\*/g;
      let lastIdx = 0;
      let m;
      let hasItalic = false;

      while ((m = italicRegex.exec(part.text)) !== null) {
        hasItalic = true;
        if (m.index > lastIdx) {
          finalParts.push({ text: part.text.substring(lastIdx, m.index), processed: false });
        }
        finalParts.push({ text: m[1], marks: { italic: true }, processed: true });
        lastIdx = m.index + m[0].length;
      }

      if (hasItalic) {
        if (lastIdx < part.text.length) {
          finalParts.push({ text: part.text.substring(lastIdx), processed: false });
        }
      } else {
        finalParts.push(part);
      }
    }
  });

  parts = finalParts.length > 0 ? finalParts : parts;

  // Fourth pass: Process __text__ (underline) only in unprocessed parts
  const underlineParts = [];
  parts.forEach((part) => {
    if (part.processed) {
      underlineParts.push(part);
    } else {
      const underlineRegex = /__([^_]+)__/g;
      let lastIdx = 0;
      let m;
      let hasUnderline = false;

      while ((m = underlineRegex.exec(part.text)) !== null) {
        hasUnderline = true;
        if (m.index > lastIdx) {
          underlineParts.push({ text: part.text.substring(lastIdx, m.index), processed: false });
        }
        underlineParts.push({ text: m[1], marks: { underline: true }, processed: true });
        lastIdx = m.index + m[0].length;
      }

      if (hasUnderline) {
        if (lastIdx < part.text.length) {
          underlineParts.push({ text: part.text.substring(lastIdx), processed: false });
        }
      } else {
        underlineParts.push(part);
      }
    }
  });

  // Convert to final node format
  underlineParts.forEach((part) => {
    if (part.text) {
      if (part.marks) {
        nodes.push({ text: part.text, ...part.marks });
      } else {
        nodes.push({ text: part.text });
      }
    }
  });

  // If no nodes were created, return plain text
  if (nodes.length === 0) {
    nodes.push({ text: text });
  }

  return nodes;
};

/**
 * Plugin to handle Markdown pasting as fallback
 *
 * @param {Editor} editor - The Slate editor instance
 * @returns {Editor} The modified editor
 */
const withMarkdown = (editor) => {
  const { insertData } = editor;

  editor.insertData = (data) => {
    const text = data.getData("text/plain");
    const html = data.getData("text/html");

    // Check if text contains markdown syntax
    if (text) {
      const hasMarkdown = /(\*\*\*|\*\*|\*|__|_|\d+\.\s+|^[*-]\s+)/m.test(text);

      if (hasMarkdown) {
        // Process markdown if:
        // 1. No HTML present, OR
        // 2. HTML is present but it's just wrapper tags (like <pre>, <meta>) with no actual formatting
        let shouldProcessMarkdown = !html;

        if (html && !shouldProcessMarkdown) {
          // Check if HTML has actual formatting tags
          const hasFormattingTags = /<(strong|b|em|i|u|ol|ul|li|h[1-6])\b/i.test(html);
          const hasFormattingStyles =
            /<span[^>]*style="[^"]*(?:font-weight:\s*(?:700|bold)|font-style:\s*italic|text-decoration[^"]*underline)/i.test(
              html
            );

          if (!hasFormattingTags && !hasFormattingStyles) {
            shouldProcessMarkdown = true;
          }
        }

        if (shouldProcessMarkdown) {
          const fragment = parseMarkdown(text);

          if (fragment && Array.isArray(fragment)) {
            Transforms.insertFragment(editor, fragment);
            return;
          }
        }
      }
    }

    insertData(data);
  };

  return editor;
};

export default withMarkdown;
